import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Heart, MessageCircle } from 'lucide-react';
import api from '../services/api';
import getBookCoverUrl from '../utils/getBookCoverUrl';
import '../styles/MyStories.css';

function MyStories() {
  const [activeTab, setActiveTab] = useState('published');
  const [stories, setStories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState(null);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [password, setPassword] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStories = async () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        // MyStories API'sinden temel veriyi al
        const myStoriesResponse = await api.get(`/my-story/my-stories?status=${activeTab}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (myStoriesResponse.data.message) {
          setError(myStoriesResponse.data.message);
          setStories([]);
        } else {
          setError(null);
          
          // Profile API'sinden doğru görüntülenme sayılarını al
          const profileResponse = await api.get(`/profile/${userId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          const profileBooks = profileResponse.data.books || [];
          
          const processedStories = myStoriesResponse.data.map(story => {
            // Profile API'sinden bu hikaye için doğru görüntülenme sayısını bul
            const profileBook = profileBooks.find(book => book.book_id === story.book_id);
            
            return {
              ...story,
              cover_image_url: getBookCoverUrl(story.cover_image_url),
              // Profile API'sinden gelen doğru görüntülenme sayısını kullan
              total_views: profileBook ? profileBook.total_views : story.total_views
            };
          });
          
          setStories(processedStories);
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Hikayeler getirilirken hata oluştu:', error);
        setError('Hikayeler getirilemedi. Lütfen tekrar deneyin.');
        setIsLoading(false);

        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        }
      }
    };

    fetchStories();
  }, [activeTab, navigate]);

  const handleStoryClick = (storyId) => {
    navigate(`/story-details/${storyId}`);
  };

  const showDeleteConfirmation = (storyId) => {
    setStoryToDelete(storyId);
    setShowConfirmation(true);
  };

  const confirmDelete = () => {
    setShowPasswordPrompt(true);
    setShowConfirmation(false);
  };

  const handlePasswordSubmit = async () => {
    if (password) {
      try {
        const token = localStorage.getItem('token');
        await api.delete(`/my-story/my-stories/${storyToDelete}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          data: { password }
        });
        setStories(prevStories => prevStories.filter(story => story.book_id !== storyToDelete));
      } catch (error) {
        console.error('Failed to delete the story:', error);
        if (error.response && error.response.data && error.response.data.message) {
          alert(error.response.data.message);
        }
      }
    } else {
      alert('Please enter your password!');
    }
    closePasswordPrompt();
  };

  const closeConfirmation = () => {
    setShowConfirmation(false);
    setStoryToDelete(null);
  };

  const closePasswordPrompt = () => {
    setShowPasswordPrompt(false);
    setPassword('');
  };

  const handleRemoveFromPublished = async (storyId) => {
    try {
      await api.put(`/my-story/my-stories/${storyId}/unpublish`, null, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setStories(prevStories => prevStories.filter(story => story.book_id !== storyId));
    } catch (error) {
      console.error('Error unpublishing story:', error);
    }
  };

  const toggleMenu = (storyId) => {
    setActiveMenu(activeMenu === storyId ? null : storyId);
  };

  const tabClass = (tab) => `tab ${activeTab === tab ? 'active' : ''}`;

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="my-stories-container">
      <div className="page-header">
        <h1>Hikayelerim</h1>
        {/* <button className="new-story-btn">Yeni Hikaye Yaz</button> */}
      </div>

      <div className="tabs">
        <button className={tabClass('published')} onClick={() => setActiveTab('published')}>Yayımlandı</button>
        <button className={tabClass('all')} onClick={() => setActiveTab('all')}>Tüm Hikayeler</button>
      </div>

      {error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="story-grid">
          {stories.map(story => (
            <div key={story.book_id} className="story-card">
              <div className="story-menu-container">
                <div className="storyedit" onClick={() => toggleMenu(story.book_id)}>
                  &#8942;
                </div>
                {activeMenu === story.book_id && (
                  <div className="story-menu">
                    {activeTab === 'published' && (
                      <div className="story-menu-item" onClick={() => handleRemoveFromPublished(story.book_id)}>
                        Yayımdan Kaldır
                      </div>
                    )}
                    <div className="story-menu-item storydelete" onClick={() => showDeleteConfirmation(story.book_id)}>
                      Sil
                    </div>
                  </div>
                )}
              </div>
              <div className="story-image" onClick={() => handleStoryClick(story.book_id)}>
                <img
                  src={story.cover_image_url}
                  alt={story.title}
                  onError={(e) => { e.target.onerror = null; e.target.src = '/images/default-book-cover.jpg'; }}
                />
              </div>
              <div className="story-details" onClick={() => handleStoryClick(story.book_id)}>
                <h2>{story.title}</h2>
                <p>{story.description}</p>
                <div className="story-stats">
                  <span className="stat-item">
                    <Eye size={16} className="stat-icon view-icon" /> {story.total_views}
                  </span>
                  <span className="stat-item">
                    <Heart size={16} className="stat-icon like-icon" /> {story.total_likes}
                  </span>
                  <span className="stat-item">
                    <MessageCircle size={16} className="stat-icon comment-icon" /> {story.total_comments}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showConfirmation || showPasswordPrompt) && (
        <div className="modal-overlay" onClick={showConfirmation ? closeConfirmation : closePasswordPrompt}></div>
      )}

      {showConfirmation && (
        <div className="confirmation-dialog">
          <p>Silmek istediğinize emin misiniz?</p>
          <div className="dialog-buttons">
            <button className="no-button" onClick={closeConfirmation}>Hayır</button>
            <button className="yes-button" onClick={confirmDelete}>Evet, Eminim</button>
          </div>
        </div>
      )}

      {showPasswordPrompt && (
        <div className="confirmation-dialog">
          <p>Silmek için şifrenizi girin:</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Şifreniz"
          />
          <div className="dialog-buttons">
             <button className="no-button" onClick={closePasswordPrompt}>İptal</button>
             <button className="yes-button" onClick={handlePasswordSubmit}>Onayla ve Sil</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyStories;
